import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  updateApplication,
  syncInAppPurchasesFromAppStore,
  createIAPPricingUploadJob,
  triggerJob,
  translateKeyValue,
  PLATFORM_IOS,
  PLATFORM_ANDROID,
  type PlatformPricing,
  type PlatformAvailability,
  type InAppProduct,
  type PricingAdviceSuggestion,
} from '../api/client';
import { AppError } from '../api/request';
import { useApplication } from '../context/ApplicationContext';
import { Check, ChevronDown, Copy, DollarSign, Info, Pencil, Plus, RotateCw, Save, Sparkles, Tag, Trash2, Upload } from 'lucide-react';
import clsx from 'clsx';
import { useError } from '../context/ErrorContext';
import { panelPath } from '../config/paths';
import { useIsPro } from '../hooks/useIsPro';
import AppPageLayout from './AppPageLayout';
import PageToolbar from './PageToolbar';
import ProTag from './ProTag';
import ModalLayout from './ModalLayout';
import PricingAIChatPanel from './PricingAIChatPanel';
import { TargetLocaleChecklist } from './SourceTargetLocalePicker';
import OnboardingTour from './onboarding/OnboardingTour';
import {
  APP_STORE_LOCALIZATIONS,
  DEFAULT_APP_STORE_LOCALE,
  getLocaleFlag,
} from '../constants/appStoreLocalizations';
import { APP_PRICE_TIERS, SUBSCRIPTION_DURATIONS } from '../constants/priceTiers';
import { APP_STORE_TERRITORIES } from '../constants/appStoreTerritories';
import { REGION_PRICE_TIERS, regionTierTerritoryLabels } from '../constants/regionPriceTiers';
import { PLAY_STORE_COUNTRIES } from '../constants/playStoreCountries';

/** key=value satırlarını map'e çevirir (ilk '=' üzerinden). */
function parseKeyValueLines(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const idx = trimmed.indexOf('=');
    if (idx <= 0) continue;
    out[trimmed.slice(0, idx).trim()] = trimmed.slice(idx + 1);
  }
  return out;
}

function productIntroPriceTier(prod: InAppProduct, primaryLocale: string): string | undefined {
  const tier =
    prod.introPriceTierByLocale?.[primaryLocale] ??
    prod.introPriceTierByLocale?.['en-US'] ??
    Object.values(prod.introPriceTierByLocale ?? {})[0];
  if (!tier || tier === 'free') return undefined;
  return tier;
}

/** Bir fiyat tier değerini gösterilebilir etikete çevirir ('free' → yerelleştirilmiş "Ücretsiz"). */
function priceTierLabel(value: string | undefined, t: (key: string) => string): string {
  if (!value || value === 'free') return t('priceTiers.free');
  const tier = APP_PRICE_TIERS.find(x => x.value === value);
  return tier?.label ?? value;
}

/** İlk ücretli tier ($0.99). Ücretsiz→Ücretli geçişinde varsayılan olarak seçilir. */
const FIRST_PAID_TIER = APP_PRICE_TIERS.find(x => x.value !== 'free')?.value ?? 'tier_1';

/** Uygulama primaryLanguage değerini App Store locale kataloğuna çözümler. */
function resolvePrimaryLocale(primaryLanguage?: string): string {
  const raw = (primaryLanguage || '').trim();
  if (!raw) return DEFAULT_APP_STORE_LOCALE;
  if (APP_STORE_LOCALIZATIONS.some(l => l.value === raw)) return raw;
  const prefix = raw.split('-')[0];
  const match = APP_STORE_LOCALIZATIONS.find(
    l => l.value === prefix || l.value.startsWith(`${prefix}-`)
  );
  return match?.value ?? DEFAULT_APP_STORE_LOCALE;
}

type StoreTab = 'pricing' | 'availability';

function getStoreTabFromPath(pathname: string): StoreTab | null {
  if (pathname.endsWith('/pricing')) return 'pricing';
  if (pathname.endsWith('/availability')) return 'availability';
  return null;
}
type PlatformAvailabilityForm = {
  mode: 'all' | 'selected';
  territories: string[];
  availableInNewTerritories: boolean;
  includeRestOfWorld: boolean;
};

const emptyPlatformAvailability = (): PlatformAvailabilityForm => ({
  mode: 'all',
  territories: [],
  availableInNewTerritories: true,
  includeRestOfWorld: false,
});

function formAvailabilityToApi(
  formAvailability: Record<string, PlatformAvailabilityForm>
): Record<string, PlatformAvailability> {
  const out: Record<string, PlatformAvailability> = {};
  for (const platform of [PLATFORM_IOS, PLATFORM_ANDROID]) {
    const a = formAvailability[platform] ?? emptyPlatformAvailability();
    out[platform] = {
      mode: a.mode,
      territories: a.mode === 'selected' ? [...a.territories] : [],
      availableInNewTerritories: platform === PLATFORM_IOS ? a.availableInNewTerritories : undefined,
      includeRestOfWorld: platform === PLATFORM_ANDROID ? a.includeRestOfWorld : undefined,
    };
  }
  return out;
}

function availabilityToForm(
  source: { availability?: Record<string, PlatformAvailability> } | null | undefined
): Record<string, PlatformAvailabilityForm> {
  const out: Record<string, PlatformAvailabilityForm> = {
    [PLATFORM_IOS]: emptyPlatformAvailability(),
    [PLATFORM_ANDROID]: emptyPlatformAvailability(),
  };
  for (const platform of [PLATFORM_IOS, PLATFORM_ANDROID]) {
    const av = source?.availability?.[platform];
    if (!av) continue;
    out[platform] = {
      mode: av.mode === 'selected' ? 'selected' : 'all',
      territories: [...(av.territories ?? [])],
      availableInNewTerritories: av.availableInNewTerritories ?? true,
      includeRestOfWorld: av.includeRestOfWorld ?? false,
    };
  }
  return out;
}

type PlatformPricingForm = {
  mainAppPriceTier: string;
  appPriceByRegionTier: Record<string, string>;
  inAppProducts: InAppProduct[];
};

const emptyPlatformPricing = (): PlatformPricingForm => ({
  mainAppPriceTier: 'free',
  appPriceByRegionTier: {},
  inAppProducts: [],
});

function deriveMainAppPriceTier(
  appPriceTierByLocale: Record<string, string> | undefined,
  primaryLanguage: string
): string {
  if (!appPriceTierByLocale || Object.keys(appPriceTierByLocale).length === 0) return 'free';
  const primary =
    appPriceTierByLocale[primaryLanguage] ??
    appPriceTierByLocale['en-US'] ??
    appPriceTierByLocale['en'];
  if (primary) return primary;
  const counts: Record<string, number> = {};
  for (const tier of Object.values(appPriceTierByLocale)) {
    counts[tier] = (counts[tier] ?? 0) + 1;
  }
  let best = 'free';
  let max = 0;
  for (const [tier, n] of Object.entries(counts)) {
    if (n > max) {
      max = n;
      best = tier;
    }
  }
  return best;
}

function formPricingToApi(formPricing: Record<string, PlatformPricingForm>): Record<string, PlatformPricing> {
  const out: Record<string, PlatformPricing> = {};
  for (const platform of [PLATFORM_IOS, PLATFORM_ANDROID]) {
    const p = formPricing[platform] ?? emptyPlatformPricing();
    const main = p.mainAppPriceTier || 'free';
    const appPriceTierByLocale: Record<string, string> = {};
    for (const loc of APP_STORE_LOCALIZATIONS.map(l => l.value)) {
      appPriceTierByLocale[loc] = main;
    }
    out[platform] = {
      appPriceTierByLocale,
      appPriceByRegionTier: p.appPriceByRegionTier ?? {},
      inAppProducts: p.inAppProducts ?? [],
    };
  }
  return out;
}

function pricingToForm(
  source: { pricing?: Record<string, PlatformPricing> } | null | undefined,
  primaryLanguage = 'en'
): Record<string, PlatformPricingForm> {
  const pricing: Record<string, PlatformPricingForm> = {
    [PLATFORM_IOS]: emptyPlatformPricing(),
    [PLATFORM_ANDROID]: emptyPlatformPricing(),
  };
  if (!source?.pricing) return pricing;
  for (const platform of [PLATFORM_IOS, PLATFORM_ANDROID]) {
    const pr = source.pricing[platform];
    if (!pr) continue;
    const main = deriveMainAppPriceTier(pr.appPriceTierByLocale, primaryLanguage);
    pricing[platform] = {
      mainAppPriceTier: main,
      appPriceByRegionTier: { ...(pr.appPriceByRegionTier ?? {}) },
      inAppProducts: (pr.inAppProducts ?? []).map(p => ({
        productId: p.productId,
        type: p.type,
        nameLocalized: { ...(p.nameLocalized ?? {}) },
        descriptionLocalized: { ...(p.descriptionLocalized ?? {}) },
        priceTierByLocale: { ...(p.priceTierByLocale ?? {}) },
        priceByRegionTier: { ...(p.priceByRegionTier ?? {}) },
        subscriptionDuration: p.subscriptionDuration,
        trialDays: p.trialDays,
        introPriceTierByLocale: { ...(p.introPriceTierByLocale ?? {}) },
        uploaded: Boolean(p.uploaded),
        uploadedStatusLabel: p.uploadedStatusLabel,
        // iOS: not on App Store Connect ⇒ cannot be on sale in the app.
        activeOnSale: Boolean(p.activeOnSale) && (platform !== PLATFORM_IOS || Boolean(p.uploaded)),
      })),
    };
  }
  return pricing;
}

type LedStatus = 'live' | 'missing_data' | 'not_synced';

/** Small traffic-light status dot with a portal tooltip so it always renders on top, never clipped. */
function StatusLed({ status, tooltip }: { status: LedStatus; tooltip: string }) {
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const dotRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    const el = dotRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ x: r.left + r.width / 2, y: r.top });
  };
  const hide = () => setCoords(null);

  return (
    <span
      ref={dotRef}
      role="img"
      aria-label={tooltip}
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      className="relative -m-1 inline-flex shrink-0 items-center justify-center p-1 cursor-help outline-none"
    >
      <span
        className={clsx(
          'h-2 w-2 rounded-full ring-2',
          status === 'live' && 'bg-green-500 ring-green-500/25',
          status === 'missing_data' && 'bg-amber-400 ring-amber-400/30',
          status === 'not_synced' && 'bg-red-500 ring-red-500/25'
        )}
      />
      {coords &&
        tooltip &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            role="tooltip"
            style={{ position: 'fixed', left: coords.x, top: coords.y - 6, zIndex: 9999 }}
            className="pointer-events-none -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg dark:bg-gray-700"
          >
            {tooltip}
          </span>,
          document.body
        )}
    </span>
  );
}

type FormState = {
  pricing: Record<string, PlatformPricingForm>;
  availability: Record<string, PlatformAvailabilityForm>;
};

const emptyForm = (): FormState => ({
  pricing: {
    [PLATFORM_IOS]: emptyPlatformPricing(),
    [PLATFORM_ANDROID]: emptyPlatformPricing(),
  },
  availability: {
    [PLATFORM_IOS]: emptyPlatformAvailability(),
    [PLATFORM_ANDROID]: emptyPlatformAvailability(),
  },
});

/** T2-T4 bölgesel fiyat override editörü. Boş bırakılan tier ana (T1) fiyatı kullanır. */
/**
 * T2-T4 bölgesel fiyat override'ları — varsayılan olarak kapalı. Kullanıcı özelleştirmek
 * istemezse hiçbir şey yapmaz ve eskisi gibi tek/genel fiyat (T1) her yerde geçerli olur.
 * Zaten kayıtlı override varsa bölüm açık başlar, aksi halde bir tıkla açılır.
 */
const RegionTierPriceEditor: React.FC<{
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const filledCount = Object.values(value).filter(v => v).length;
  const [sectionOpen, setSectionOpen] = useState(filledCount > 0);
  const [expandedTier, setExpandedTier] = useState<string | null>(null);

  return (
    <div>
      <button
        type="button"
        onClick={() => setSectionOpen(prev => !prev)}
        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
      >
        {sectionOpen ? t('pricing.regionTierHideSection') : t('pricing.regionTierShowSection')}
        {filledCount > 0 && (
          <span className="font-normal text-gray-500 dark:text-gray-400">
            ({t('pricing.regionTierFilledCount', { count: filledCount })})
          </span>
        )}
      </button>
      {sectionOpen && (
        <div className="mt-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('pricing.regionTierIntro')}</p>
          {REGION_PRICE_TIERS.map(tier => {
            const current = value[tier.value] ?? '';
            const expanded = expandedTier === tier.value;
            return (
              <div key={tier.value} className="flex flex-col gap-1.5">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{tier.label}</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{tier.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={current}
                    onChange={e => {
                      const val = e.target.value;
                      const next = { ...value };
                      if (val) next[tier.value] = val;
                      else delete next[tier.value];
                      onChange(next);
                    }}
                    className="flex-1 min-w-0 max-w-xs px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                  >
                    <option value="">{t('pricing.regionTierUseT1')}</option>
                    {APP_PRICE_TIERS.map(tierOption => (
                      <option key={tierOption.value} value={tierOption.value}>
                        {tierOption.labelKey ? t(tierOption.labelKey) : tierOption.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setExpandedTier(prev => (prev === tier.value ? null : tier.value))}
                    className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline shrink-0"
                  >
                    {expanded ? t('pricing.regionTierHideCountries') : t('pricing.regionTierShowCountries')}
                  </button>
                </div>
                {expanded && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {regionTierTerritoryLabels(tier).join(', ')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AppStoreSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const location = useLocation();
  const currentTab = getStoreTabFromPath(location.pathname) ?? 'pricing';
  const navigate = useNavigate();
  const { showError: showErrorPopup } = useError();
  const { isPro, goToUpgrade } = useIsPro();
  const {
    app: contextApp,
    loading: contextAppLoading,
    error: contextError,
    setApp: setContextApp,
  } = useApplication();
  // ApplicationProvider already loads GET /applications/:id; reuse it.
  const app = appId && contextApp?.id === appId ? contextApp : null;
  const loading = Boolean(appId) && contextAppLoading;
  const error = contextError;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formHydratedFor, setFormHydratedFor] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>(PLATFORM_IOS);
  const [showPricingProductModal, setShowPricingProductModal] = useState(false);
  const [editingPricingProductIndex, setEditingPricingProductIndex] = useState<number | null>(null);
  const [availabilitySearch, setAvailabilitySearch] = useState('');
  const [pricingProductForm, setPricingProductForm] = useState<InAppProduct>({
    productId: '',
    type: 'one_time',
    nameLocalized: {},
    descriptionLocalized: {},
    priceTierByLocale: {},
    priceByRegionTier: {},
    subscriptionDuration: 'P1M',
    trialDays: 0,
    introPriceTierByLocale: {},
  });
  const [showOtherLocales, setShowOtherLocales] = useState(false);
  const [showProductAiTranslateModal, setShowProductAiTranslateModal] = useState(false);
  const [productAiTargetLocales, setProductAiTargetLocales] = useState<string[]>([]);
  const [productAiTranslating, setProductAiTranslating] = useState(false);
  const [syncingIap, setSyncingIap] = useState(false);
  const [iapSyncMessage, setIapSyncMessage] = useState<string | null>(null);
  const [iapSyncError, setIapSyncError] = useState<string | null>(null);
  const [selectedUploadIds, setSelectedUploadIds] = useState<string[]>([]);
  const [creatingUploadJob, setCreatingUploadJob] = useState(false);
  const [uploadingProductId, setUploadingProductId] = useState<string | null>(null);
  const [aiAdvisorOpen, setAiAdvisorOpen] = useState(false);
  const [aiApplyNotice, setAiApplyNotice] = useState<string | null>(null);
  const aiApplyNoticeTimer = useRef<number | undefined>(undefined);

  // Clear the pending "applied" toast timer on unmount.
  useEffect(() => () => window.clearTimeout(aiApplyNoticeTimer.current), []);

  useEffect(() => {
    if (!appId) return;
    const tab = getStoreTabFromPath(location.pathname);
    if (!tab) {
      navigate(panelPath(`/applications/${appId}/pricing`), { replace: true });
      return;
    }
    if (tab === 'pricing' && !isPro) {
      navigate(panelPath('upgrade'), { replace: true });
    }
  }, [appId, location.pathname, navigate, isPro]);

  useEffect(() => {
    if (!app || !appId) return;
    if (formHydratedFor === appId) return;
    setForm({
      pricing: pricingToForm(app, app.primaryLanguage || 'en'),
      availability: availabilityToForm(app),
    });
    setFormHydratedFor(appId);
  }, [app, appId, formHydratedFor]);

  useEffect(() => {
    setFormHydratedFor(null);
  }, [appId]);

  const currentPlatformPricing = useMemo(
    () => form.pricing[selectedPlatform] ?? emptyPlatformPricing(),
    [form.pricing, selectedPlatform]
  );

  const currentPlatformAvailability = useMemo(
    () => form.availability[selectedPlatform] ?? emptyPlatformAvailability(),
    [form.availability, selectedPlatform]
  );

  const isMissingOnStore = (prod: InAppProduct): boolean =>
    selectedPlatform === PLATFORM_IOS && !prod.uploaded;

  const unsyncedFormProducts = useMemo(() => {
    if (selectedPlatform !== PLATFORM_IOS) return [];
    return currentPlatformPricing.inAppProducts.filter(p => !p.uploaded);
  }, [selectedPlatform, currentPlatformPricing.inAppProducts]);

  const formMissingCount = unsyncedFormProducts.length;

  const availabilityOptions = useMemo(
    () => (selectedPlatform === PLATFORM_IOS ? APP_STORE_TERRITORIES : PLAY_STORE_COUNTRIES),
    [selectedPlatform]
  );

  const filteredAvailabilityOptions = useMemo(() => {
    const q = availabilitySearch.trim().toLowerCase();
    if (!q) return availabilityOptions;
    return availabilityOptions.filter(
      o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [availabilityOptions, availabilitySearch]);

  const primaryLocale = useMemo(
    () => resolvePrimaryLocale(app?.primaryLanguage),
    [app]
  );

  const primaryLocaleLabel = useMemo(
    () => APP_STORE_LOCALIZATIONS.find(l => l.value === primaryLocale)?.label ?? primaryLocale,
    [primaryLocale]
  );

  const otherLocales = useMemo(
    () => APP_STORE_LOCALIZATIONS.filter(l => l.value !== primaryLocale),
    [primaryLocale]
  );

  // Live pricing payload for the AI advisor — reflects the current (unsaved) form state
  // so the assistant evaluates exactly what the user is looking at.
  const aiAdvisorPricing = useMemo(
    () => ({
      currency: app?.pricing?.[selectedPlatform]?.appPriceCurrency,
      appPriceTier: currentPlatformPricing.mainAppPriceTier,
      products: currentPlatformPricing.inAppProducts.map(p => ({
        productId: p.productId,
        type: p.type,
        name: p.nameLocalized?.[primaryLocale] ?? Object.values(p.nameLocalized ?? {})[0],
        priceTier: p.priceTierByLocale?.[primaryLocale] ?? Object.values(p.priceTierByLocale ?? {})[0],
        subscriptionDuration: p.subscriptionDuration,
        trialDays: p.trialDays,
        activeOnSale: p.activeOnSale,
      })),
    }),
    [app, selectedPlatform, currentPlatformPricing, primaryLocale]
  );

  const filledOtherLocaleCount = useMemo(() => {
    return otherLocales.filter(loc => {
      const name = (pricingProductForm.nameLocalized?.[loc.value] ?? '').trim();
      const desc = (pricingProductForm.descriptionLocalized?.[loc.value] ?? '').trim();
      const price = pricingProductForm.priceTierByLocale?.[loc.value];
      return name !== '' || desc !== '' || (price != null && price !== '' && price !== 'free');
    }).length;
  }, [otherLocales, pricingProductForm]);

  const primaryHasContent = useMemo(() => {
    const name = (pricingProductForm.nameLocalized?.[primaryLocale] ?? '').trim();
    const desc = (pricingProductForm.descriptionLocalized?.[primaryLocale] ?? '').trim();
    return name !== '' || desc !== '';
  }, [pricingProductForm, primaryLocale]);

  const handleOpenPricingProductModal = (index: number | null) => {
    if (index !== null && currentPlatformPricing.inAppProducts[index]) {
      const p = currentPlatformPricing.inAppProducts[index];
      setPricingProductForm({
        productId: p.productId,
        type: p.type,
        nameLocalized: { ...(p.nameLocalized ?? {}) },
        descriptionLocalized: { ...(p.descriptionLocalized ?? {}) },
        priceTierByLocale: { ...(p.priceTierByLocale ?? {}) },
        priceByRegionTier: { ...(p.priceByRegionTier ?? {}) },
        subscriptionDuration: p.subscriptionDuration ?? 'P1M',
        trialDays: p.trialDays ?? 0,
        introPriceTierByLocale: { ...(p.introPriceTierByLocale ?? {}) },
        uploaded: Boolean(p.uploaded),
        uploadedStatusLabel: p.uploadedStatusLabel,
        activeOnSale: Boolean(p.activeOnSale),
      });
    } else {
      setPricingProductForm({
        productId: '',
        type: 'one_time',
        nameLocalized: {},
        descriptionLocalized: {},
        priceTierByLocale: {},
        priceByRegionTier: {},
        subscriptionDuration: 'P1M',
        trialDays: 0,
        introPriceTierByLocale: {},
        uploaded: false,
      });
    }
    setEditingPricingProductIndex(index);
    setShowOtherLocales(false);
    setShowPricingProductModal(true);
  };

  const handleCopyPricingProduct = (index: number) => {
    const source = currentPlatformPricing.inAppProducts[index];
    if (!source) return;
    const existingIds = new Set(currentPlatformPricing.inAppProducts.map(p => p.productId));
    const suffix = t('pricing.copySuffix');
    const base = source.productId.trim();
    let candidate = base ? `${base}.${suffix}` : '';
    let n = 2;
    while (candidate && existingIds.has(candidate)) {
      candidate = `${base}.${suffix}${n}`;
      n += 1;
    }
    setPricingProductForm({
      productId: candidate,
      type: source.type,
      nameLocalized: { ...(source.nameLocalized ?? {}) },
      descriptionLocalized: { ...(source.descriptionLocalized ?? {}) },
      priceTierByLocale: { ...(source.priceTierByLocale ?? {}) },
      priceByRegionTier: { ...(source.priceByRegionTier ?? {}) },
      subscriptionDuration: source.subscriptionDuration ?? 'P1M',
      trialDays: source.trialDays ?? 0,
      introPriceTierByLocale: { ...(source.introPriceTierByLocale ?? {}) },
      uploaded: false,
      activeOnSale: Boolean(source.activeOnSale),
    });
    // Yeni ürün olarak aç: kaydet listeye ekler, üzerine yazmaz.
    setEditingPricingProductIndex(null);
    setShowOtherLocales(false);
    setShowPricingProductModal(true);
  };

  const handleOpenProductAiTranslateModal = () => {
    if (!isPro) {
      goToUpgrade();
      return;
    }
    if (!primaryHasContent) {
      showErrorPopup(t('pricing.aiTranslateNeedPrimary'));
      return;
    }
    setProductAiTargetLocales(otherLocales.map(l => l.value));
    setShowProductAiTranslateModal(true);
  };

  const handleProductAiTranslate = async () => {
    if (productAiTargetLocales.length === 0) {
      showErrorPopup(t('pricing.aiTranslateSelectLocales'));
      return;
    }
    const sourceName = (pricingProductForm.nameLocalized?.[primaryLocale] ?? '').trim();
    const sourceDesc = (pricingProductForm.descriptionLocalized?.[primaryLocale] ?? '').trim();
    if (!sourceName && !sourceDesc) {
      showErrorPopup(t('pricing.aiTranslateNeedPrimary'));
      return;
    }
    const lines: string[] = [];
    if (sourceName) lines.push(`name=${sourceName}`);
    if (sourceDesc) lines.push(`description=${sourceDesc}`);
    const sourcePrice = pricingProductForm.priceTierByLocale?.[primaryLocale] ?? 'free';

    setProductAiTranslating(true);
    try {
      const { results } = await translateKeyValue({
        content: lines.join('\n'),
        sourceLocale: primaryLocale,
        targetLocales: productAiTargetLocales,
      });
      setPricingProductForm(f => {
        const nameLocalized = { ...(f.nameLocalized ?? {}) };
        const descriptionLocalized = { ...(f.descriptionLocalized ?? {}) };
        const priceTierByLocale = { ...(f.priceTierByLocale ?? {}) };
        for (const locale of productAiTargetLocales) {
          const parsed = parseKeyValueLines(results[locale] ?? '');
          if (parsed.name != null && parsed.name.trim() !== '') {
            nameLocalized[locale] = parsed.name.trim();
          }
          if (parsed.description != null && parsed.description.trim() !== '') {
            descriptionLocalized[locale] = parsed.description.trim();
          }
          priceTierByLocale[locale] = sourcePrice;
        }
        return { ...f, nameLocalized, descriptionLocalized, priceTierByLocale };
      });
      setShowProductAiTranslateModal(false);
      setShowOtherLocales(true);
    } catch (e) {
      showErrorPopup(e instanceof Error ? e.message : t('appStoreSettings.aiTranslationFailed'));
    } finally {
      setProductAiTranslating(false);
    }
  };

  const handleSavePricingProduct = () => {
    if (!pricingProductForm.productId.trim()) {
      showErrorPopup(t('appStoreSettings.productIdRequired'));
      return;
    }
    const products = [...(currentPlatformPricing.inAppProducts ?? [])];
    const newProduct: InAppProduct = {
      productId: pricingProductForm.productId.trim(),
      type: pricingProductForm.type,
      nameLocalized: { ...(pricingProductForm.nameLocalized ?? {}) },
      descriptionLocalized: { ...(pricingProductForm.descriptionLocalized ?? {}) },
      priceTierByLocale: { ...(pricingProductForm.priceTierByLocale ?? {}) },
      priceByRegionTier: { ...(pricingProductForm.priceByRegionTier ?? {}) },
      subscriptionDuration:
        pricingProductForm.type === 'subscription' ? pricingProductForm.subscriptionDuration : undefined,
      trialDays:
        pricingProductForm.type === 'subscription' && (pricingProductForm.trialDays ?? 0) > 0
          ? pricingProductForm.trialDays
          : undefined,
      introPriceTierByLocale:
        pricingProductForm.type === 'subscription' &&
        productIntroPriceTier(pricingProductForm, primaryLocale)
          ? { ...(pricingProductForm.introPriceTierByLocale ?? {}) }
          : undefined,
      uploaded: editingPricingProductIndex !== null ? Boolean(pricingProductForm.uploaded) : false,
      uploadedStatusLabel: pricingProductForm.uploadedStatusLabel,
      activeOnSale: Boolean(pricingProductForm.activeOnSale),
    };
    if (editingPricingProductIndex !== null) {
      products[editingPricingProductIndex] = newProduct;
    } else {
      products.push(newProduct);
    }
    setForm(f => ({
      ...f,
      pricing: {
        ...f.pricing,
        [selectedPlatform]: {
          ...(f.pricing[selectedPlatform] ?? emptyPlatformPricing()),
          inAppProducts: products,
        },
      },
    }));
    setShowPricingProductModal(false);
    setEditingPricingProductIndex(null);
  };

  const handleRemovePricingProduct = (index: number) => {
    const products = currentPlatformPricing.inAppProducts.filter((_, i) => i !== index);
    setForm(f => ({
      ...f,
      pricing: {
        ...f.pricing,
        [selectedPlatform]: {
          ...(f.pricing[selectedPlatform] ?? emptyPlatformPricing()),
          inAppProducts: products,
        },
      },
    }));
  };

  // Apply the AI advisor's structured suggestions to the live pricing form for the current
  // platform: a suggestion whose productId matches an existing product updates it (primary-locale
  // price + subscription fields); otherwise a new product is created. Nothing is saved here —
  // the user reviews and persists with the page's Save action.
  const handleApplyPricingSuggestions = (suggestions: PricingAdviceSuggestion[]) => {
    if (!suggestions || suggestions.length === 0) return;
    let updated = 0;
    let created = 0;
    setForm(f => {
      const platformPricing = f.pricing[selectedPlatform] ?? emptyPlatformPricing();
      const products = platformPricing.inAppProducts.map(p => ({ ...p }));
      for (const s of suggestions) {
        const productId = (s.productId ?? '').trim();
        if (!productId) continue;
        const idx = products.findIndex(p => p.productId === productId);
        if (idx >= 0) {
          const prod = products[idx];
          const next: InAppProduct = { ...prod };
          if (s.priceTier) {
            next.priceTierByLocale = { ...(prod.priceTierByLocale ?? {}), [primaryLocale]: s.priceTier };
          }
          if (next.type === 'subscription') {
            if (s.subscriptionDuration) next.subscriptionDuration = s.subscriptionDuration;
            if (typeof s.trialDays === 'number') next.trialDays = s.trialDays;
          }
          products[idx] = next;
          updated += 1;
        } else {
          const type: InAppProduct['type'] = s.type === 'subscription' ? 'subscription' : 'one_time';
          products.push({
            productId,
            type,
            nameLocalized: s.name ? { [primaryLocale]: s.name } : {},
            descriptionLocalized: {},
            priceTierByLocale: s.priceTier ? { [primaryLocale]: s.priceTier } : {},
            priceByRegionTier: {},
            subscriptionDuration: type === 'subscription' ? s.subscriptionDuration || 'P1M' : undefined,
            trialDays: type === 'subscription' ? s.trialDays ?? 0 : undefined,
            activeOnSale: false,
            uploaded: false,
          });
          created += 1;
        }
      }
      return {
        ...f,
        pricing: {
          ...f.pricing,
          [selectedPlatform]: { ...platformPricing, inAppProducts: products },
        },
      };
    });
    setAiApplyNotice(t('pricing.aiAdvisorApplied', { updated, created }));
    window.clearTimeout(aiApplyNoticeTimer.current);
    aiApplyNoticeTimer.current = window.setTimeout(() => setAiApplyNotice(null), 4000);
  };

  const updateCurrentPricing = (patch: Partial<PlatformPricingForm>) =>
    setForm(f => ({
      ...f,
      pricing: {
        ...f.pricing,
        [selectedPlatform]: {
          ...(f.pricing[selectedPlatform] ?? emptyPlatformPricing()),
          ...patch,
        },
      },
    }));

  const toggleProductActiveOnSale = (index: number) => {
    const products = currentPlatformPricing.inAppProducts.map((p, i) => {
      if (i !== index) return p;
      const next = !p.activeOnSale;
      // iOS: cannot mark for sale until the product exists on App Store Connect.
      if (next && selectedPlatform === PLATFORM_IOS && !p.uploaded) {
        return p;
      }
      return { ...p, activeOnSale: next };
    });
    updateCurrentPricing({ inAppProducts: products });
  };

  const renderProductRow = (prod: InAppProduct, idx: number) => {
    const notSynced = isMissingOnStore(prod);
    const onSale =
      Boolean(prod.activeOnSale) && !(selectedPlatform === PLATFORM_IOS && !prod.uploaded);
    const saleToggleDisabled = selectedPlatform === PLATFORM_IOS && !prod.uploaded;
    const showStatus = prod.uploaded || selectedPlatform === PLATFORM_IOS;
    const primaryName = (prod.nameLocalized?.[primaryLocale] ?? '').trim();
    const primaryPrice = prod.priceTierByLocale?.[primaryLocale];
    const dataMissing =
      showStatus && !notSynced && (primaryName === '' || primaryPrice == null || primaryPrice === '');
    const ledStatus: LedStatus | null = !showStatus
      ? null
      : notSynced
        ? 'not_synced'
        : dataMissing
          ? 'missing_data'
          : 'live';
    const ledTooltip =
      ledStatus === 'missing_data'
        ? t('pricing.statusMissingDataHint')
        : prod.uploadedStatusLabel ||
          (ledStatus === 'not_synced' ? t('pricing.statusNotSyncedHint') : t('pricing.statusLiveHint'));
    return (
      <li
        key={`${prod.productId}-${idx}`}
        className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50"
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedPlatform === PLATFORM_IOS && (
            <input
              type="checkbox"
              checked={selectedUploadIds.includes(prod.productId)}
              onChange={() => toggleUploadSelection(prod.productId)}
              className="rounded border-gray-400 dark:border-gray-500 text-primary-600 shrink-0"
            />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              {ledStatus && <StatusLed status={ledStatus} tooltip={ledTooltip} />}
              <span className="font-mono text-sm font-medium text-gray-900 dark:text-white truncate">
                {prod.productId}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                role="switch"
                aria-checked={onSale}
                aria-disabled={saleToggleDisabled}
                disabled={saleToggleDisabled}
                onClick={() => toggleProductActiveOnSale(idx)}
                title={
                  saleToggleDisabled
                    ? t('pricing.activeOnSaleRequiresStore')
                    : t('pricing.activeOnSaleHint')
                }
                className={clsx(
                  'group inline-flex items-center gap-1.5 rounded-full border px-1.5 py-0.5 text-xs font-medium transition-colors',
                  saleToggleDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
                  onSale
                    ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50'
                    : 'border-dashed border-gray-300 bg-transparent text-gray-500 hover:border-gray-400 hover:text-gray-700 dark:border-gray-500 dark:text-gray-400 dark:hover:border-gray-400 dark:hover:text-gray-200',
                  saleToggleDisabled &&
                    'hover:border-gray-300 hover:text-gray-500 dark:hover:border-gray-500 dark:hover:text-gray-400'
                )}
              >
                <span
                  className={clsx(
                    'relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full transition-colors',
                    onSale
                      ? 'bg-green-500'
                      : 'bg-gray-300 group-hover:bg-gray-400 dark:bg-gray-600 dark:group-hover:bg-gray-500'
                  )}
                  aria-hidden
                >
                  <span
                    className={clsx(
                      'inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow transition-transform',
                      onSale ? 'translate-x-3' : 'translate-x-0.5'
                    )}
                  />
                </span>
                {onSale ? t('pricing.activeOnSale') : t('pricing.notOnSale')}
              </button>
              <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                {priceTierLabel(
                  prod.priceTierByLocale?.[primaryLocale] ??
                    Object.values(prod.priceTierByLocale ?? {})[0],
                  t
                )}
              </span>
              {prod.type === 'subscription' && prod.subscriptionDuration && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {(() => {
                    const duration = SUBSCRIPTION_DURATIONS.find(d => d.value === prod.subscriptionDuration);
                    return duration ? t(duration.labelKey) : prod.subscriptionDuration;
                  })()}
                </span>
              )}
              {prod.type === 'subscription' && (prod.trialDays ?? 0) > 0 && (
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                  {t('pricing.trialBadge', { count: prod.trialDays })}
                </span>
              )}
              {prod.type === 'subscription' && productIntroPriceTier(prod, primaryLocale) && (
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  {t('pricing.introPriceBadge', {
                    price: priceTierLabel(productIntroPriceTier(prod, primaryLocale), t),
                  })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {selectedPlatform === PLATFORM_IOS && (
            <button
              type="button"
              onClick={() => void handleCreateUploadJob([prod.productId])}
              disabled={submitting || creatingUploadJob || !prod.productId.trim()}
              title={t('pricing.uploadProductHint')}
              aria-label={t('pricing.uploadToAppConnect')}
              className="p-1.5 rounded text-gray-600 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 disabled:opacity-50 disabled:pointer-events-none"
            >
              <Upload className={clsx('h-4 w-4', uploadingProductId === prod.productId && 'animate-pulse')} />
            </button>
          )}
          <button
            type="button"
            onClick={() => handleCopyPricingProduct(idx)}
            className="p-1.5 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
            title={t('pricing.copyProductHint')}
            aria-label={t('pricing.copyProduct')}
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => handleOpenPricingProductModal(idx)}
            className="p-1.5 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
            title={t('common.edit')}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => handleRemovePricingProduct(idx)}
            className="p-1.5 rounded text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            title={t('appScreen.remove')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </li>
    );
  };

  const handleSyncIapFromAppConnect = async () => {
    if (!appId) return;
    if (selectedPlatform !== PLATFORM_IOS) {
      showErrorPopup(t('pricing.syncFromAppConnectIosOnly'));
      return;
    }
    setSyncingIap(true);
    setIapSyncMessage(null);
    setIapSyncError(null);
    try {
      const result = await syncInAppPurchasesFromAppStore(appId);
      setContextApp(result.application);
      setForm(f => ({
        ...f,
        pricing: pricingToForm(result.application, result.application.primaryLanguage || 'en'),
        availability: availabilityToForm(result.application),
      }));
      setFormHydratedFor(appId);
      setIapSyncMessage(
        t('pricing.syncFromAppConnectSuccess', {
          synced: result.productsSynced,
          added: result.productsAdded,
        })
      );
    } catch (err) {
      showErrorPopup(err instanceof AppError ? err.userMessage : err instanceof Error ? err.message : t('pricing.syncFromAppConnect'));
    } finally {
      setSyncingIap(false);
    }
  };

  const persistPricingForm = async () => {
    if (!appId) return null;
    const updated = await updateApplication(appId, {
      pricing: formPricingToApi(form.pricing),
      availability: formAvailabilityToApi(form.availability),
    });
    setContextApp(updated);
    setForm({
      pricing: pricingToForm(updated, updated.primaryLanguage || 'en'),
      availability: availabilityToForm(updated),
    });
    setFormHydratedFor(appId);
    return updated;
  };


  const toggleUploadSelection = (productId: string) => {
    setSelectedUploadIds(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const handleCreateUploadJob = async (productIds: string[]) => {
    if (!appId || productIds.length === 0) return;
    if (!isPro) {
      goToUpgrade();
      return;
    }
    setCreatingUploadJob(true);
    setUploadingProductId(productIds.length === 1 ? productIds[0] : null);
    try {
      await persistPricingForm();
      const { jobId } = await createIAPPricingUploadJob(appId, productIds);
      triggerJob(jobId).catch(() => {});
      setSelectedUploadIds([]);
      navigate(panelPath(`/jobs/${jobId}`));
    } catch (err) {
      const msg =
        err instanceof AppError ? err.userMessage : err instanceof Error ? err.message : t('pricing.uploadToAppConnect');
      showErrorPopup(msg);
    } finally {
      setCreatingUploadJob(false);
      setUploadingProductId(null);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appId) return;
    setSubmitting(true);
    try {
      await persistPricingForm();
    } catch (err) {
      showErrorPopup(err instanceof Error ? err.message : t('common.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!appId) return null;

  // Yüklenmiş (ASC'ye push edilmiş) bir ürün düzenlenirken yalnızca fiyat ve
  // offer (deneme süresi + intro fiyatı) değiştirilebilir. productId, tür,
  // abonelik süresi ve isim/açıklama gibi diğer alanlar ASC'de sabittir; bu yüzden
  // salt-okunur yapılır. Bkz. backend: AttachSubscriptionDetails / UpdateOneTimeIAPDetails.
  const isUploadedProductEdit =
    editingPricingProductIndex !== null && Boolean(pricingProductForm.uploaded);

  return (
    <AppPageLayout
      appId={appId}
      app={app}
      loading={loading}
      error={error || (!loading && !app ? t('appStoreSettings.appNotFound') : null)}
      pageTitle={currentTab === 'pricing' ? t('nav.pricing') : t('nav.availability')}
    >
      {app && (
        <>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 has-[[data-stuck]]:!rounded-t-none">
            {error && (
              <div className="mx-4 mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            <PageToolbar className="justify-between">
              <div data-tour="settings-platform-switch" className="inline-flex h-8 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-0.5">
                <button
                  type="button"
                  onClick={() => setSelectedPlatform(PLATFORM_IOS)}
                  className={`inline-flex items-center gap-1.5 h-full px-2.5 rounded-md text-sm font-medium transition-colors ${
                    selectedPlatform === PLATFORM_IOS
                      ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <span className="text-base leading-none" aria-hidden>
                    🍎
                  </span>
                  {t('appStoreSettings.ios')}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedPlatform(PLATFORM_ANDROID)}
                  className={`inline-flex items-center gap-1.5 h-full px-2.5 rounded-md text-sm font-medium transition-colors ${
                    selectedPlatform === PLATFORM_ANDROID
                      ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <span className="text-base leading-none" aria-hidden>
                    🤖
                  </span>
                  {t('appStoreSettings.android')}
                </button>
              </div>
              <div className="flex items-center gap-2">
                {currentTab === 'pricing' && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isPro) {
                        goToUpgrade();
                        return;
                      }
                      setAiAdvisorOpen(true);
                    }}
                    title={isPro ? t('pricing.aiAdvisorTitle') : t('proFeature.requiresPro')}
                    className={clsx(
                      'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors',
                      isPro
                        ? 'bg-white dark:bg-gray-900/80 text-primary-700 dark:text-primary-300 border border-primary-300/80 dark:border-primary-700 hover:bg-primary-100/80 dark:hover:bg-primary-900/40'
                        : 'bg-white/70 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 cursor-pointer'
                    )}
                  >
                    <Sparkles className="h-4 w-4 shrink-0" />
                    <span className="hidden lg:inline">{t('pricing.aiAdvisor')}</span>
                    {!isPro && <ProTag />}
                  </button>
                )}
                {currentTab === 'pricing' && selectedPlatform === PLATFORM_IOS && currentPlatformPricing.inAppProducts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => void handleCreateUploadJob(selectedUploadIds)}
                    disabled={submitting || creatingUploadJob || selectedUploadIds.length === 0 || !isPro}
                    title={t('pricing.uploadToAppConnectHint')}
                    className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                  >
                    <Upload className={`h-4 w-4 shrink-0 ${creatingUploadJob ? 'animate-pulse' : ''}`} />
                    {creatingUploadJob ? t('pricing.uploadingToAppConnect') : t('pricing.uploadToAppConnect')}
                    {selectedUploadIds.length > 0 && ` (${selectedUploadIds.length})`}
                  </button>
                )}
                <button
                  data-tour="settings-save"
                  type="submit"
                  form="app-store-settings-form"
                  disabled={submitting || (currentTab === 'pricing' && !isPro)}
                  onClick={e => {
                    if (currentTab === 'pricing' && !isPro) {
                      e.preventDefault();
                      goToUpgrade();
                    }
                  }}
                  className={
                    currentTab === 'pricing' && !isPro
                      ? 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 cursor-pointer'
                      : 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 shadow-sm disabled:opacity-50 disabled:pointer-events-none transition-colors'
                  }
                >
                  <Save className="h-4 w-4 shrink-0" />
                  {submitting ? t('common.saving') : t('common.save')}
                  {currentTab === 'pricing' && !isPro && <ProTag />}
                </button>
              </div>
            </PageToolbar>

            <form id="app-store-settings-form" onSubmit={handleSave} className="p-4 space-y-4">
              {currentTab === 'pricing' && (
                <>
                  <div className="p-0 mb-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {t('nav.pricing')}
                      <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                        ({selectedPlatform === PLATFORM_IOS ? t('version.appStoreIosLabel') : t('version.playStoreAndroidLabel')})
                      </span>
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('appStoreSettings.pricingSubtitle')}
                    </p>
                  </div>
                  <div className="space-y-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {(() => {
                      const mainTier = currentPlatformPricing.mainAppPriceTier ?? 'free';
                      const isPaidApp = mainTier !== 'free' && mainTier !== '';
                      return (
                        <section
                          data-tour="settings-price-tier"
                          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/40 p-4"
                        >
                          <div className="flex items-center gap-2.5 mb-3">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/40">
                              <DollarSign className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                            </span>
                            <div className="min-w-0">
                              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {t('pricing.mainAppPriceTitle')}
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {t('pricing.mainAppPriceSubtitle')}
                              </p>
                            </div>
                          </div>

                          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 p-0.5">
                            <button
                              type="button"
                              onClick={() => updateCurrentPricing({ mainAppPriceTier: 'free' })}
                              className={clsx(
                                'inline-flex items-center gap-1.5 h-8 px-4 rounded-md text-sm font-medium transition-colors',
                                !isPaidApp
                                  ? 'bg-primary-600 text-white shadow-sm'
                                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                              )}
                            >
                              {!isPaidApp && <Check className="h-4 w-4 shrink-0" />}
                              {t('pricing.mainAppPriceFree')}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (!isPaidApp) updateCurrentPricing({ mainAppPriceTier: FIRST_PAID_TIER });
                              }}
                              className={clsx(
                                'inline-flex items-center gap-1.5 h-8 px-4 rounded-md text-sm font-medium transition-colors',
                                isPaidApp
                                  ? 'bg-primary-600 text-white shadow-sm'
                                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                              )}
                            >
                              {isPaidApp && <Check className="h-4 w-4 shrink-0" />}
                              {t('pricing.mainAppPricePaid')}
                            </button>
                          </div>

                          {isPaidApp && (
                            <div className="mt-4 space-y-1.5">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                                {t('pricing.mainAppPriceSelectLabel')}
                              </label>
                              <select
                                value={mainTier}
                                onChange={e => updateCurrentPricing({ mainAppPriceTier: e.target.value })}
                                className="w-full max-w-xs px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                              >
                                {APP_PRICE_TIERS.filter(tier => tier.value !== 'free').map(tier => (
                                  <option key={tier.value} value={tier.value}>
                                    {tier.labelKey ? t(tier.labelKey) : tier.label}
                                  </option>
                                ))}
                              </select>
                              <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                <Info className="h-3.5 w-3.5 shrink-0 mt-px" />
                                <span>{t('pricing.mainAppPricePaidHelp')}</span>
                              </p>
                              <RegionTierPriceEditor
                                value={currentPlatformPricing.appPriceByRegionTier ?? {}}
                                onChange={next => updateCurrentPricing({ appPriceByRegionTier: next })}
                              />
                            </div>
                          )}
                        </section>
                      );
                    })()}
                    <div data-tour="settings-iap">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/40">
                            <Tag className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          </span>
                          <div className="min-w-0">
                            <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                              {t('panel.appStoreIap')}
                              {currentPlatformPricing.inAppProducts.length > 0 && (
                                <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                  {currentPlatformPricing.inAppProducts.length}
                                </span>
                              )}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('pricing.iapSectionHint')}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {selectedPlatform === PLATFORM_IOS && (
                            <>
                              <button
                                type="button"
                                onClick={() => void handleSyncIapFromAppConnect()}
                                disabled={syncingIap || creatingUploadJob}
                                title={t('pricing.syncFromAppConnectHint')}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
                              >
                                <RotateCw className={clsx('h-3.5 w-3.5', syncingIap && 'animate-spin')} />
                                {syncingIap ? t('pricing.syncingFromAppConnect') : t('pricing.syncFromAppConnect')}
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => handleOpenPricingProductModal(null)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-primary-600 text-white hover:bg-primary-700"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            {t('appStoreSettings.addProduct')}
                          </button>
                        </div>
                      </div>
                      {iapSyncMessage && selectedPlatform === PLATFORM_IOS && (
                        <p className="text-xs text-green-700 dark:text-green-400 mb-2">{iapSyncMessage}</p>
                      )}
                      {iapSyncError && selectedPlatform === PLATFORM_IOS && (
                        <p className="text-xs text-red-700 dark:text-red-300 mb-2 whitespace-pre-line">{iapSyncError}</p>
                      )}
                      {selectedPlatform === PLATFORM_IOS && formMissingCount > 0 && (
                        <div className="mb-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                          <p className="text-xs text-amber-800 dark:text-amber-200">
                            {t('pricing.notUploadedWarning', { count: formMissingCount })}
                          </p>
                        </div>
                      )}
                      {selectedPlatform === PLATFORM_IOS && currentPlatformPricing.inAppProducts.length > 0 && (
                        <div className="mb-2 flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedUploadIds(currentPlatformPricing.inAppProducts.map(p => p.productId))
                            }
                            className="text-xs font-medium text-primary-600 dark:text-primary-400 underline-offset-2 hover:underline"
                          >
                            {t('pricing.selectAllLocales')}
                          </button>
                          {selectedUploadIds.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedUploadIds([])}
                              className="text-xs font-medium text-gray-600 dark:text-gray-400 underline-offset-2 hover:underline"
                            >
                              {t('pricing.clearSelection')}
                            </button>
                          )}
                        </div>
                      )}
                      {currentPlatformPricing.inAppProducts.length === 0 ? (
                        <button
                          type="button"
                          onClick={() => handleOpenPricingProductModal(null)}
                          className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 py-6 px-4 text-center hover:border-primary-400 hover:bg-primary-50/40 dark:hover:bg-primary-900/10 transition-colors"
                        >
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400">
                            <Plus className="h-5 w-5" />
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {t('appStoreSettings.noProductsYet')}
                          </span>
                        </button>
                      ) : (
                        (() => {
                          const rows = currentPlatformPricing.inAppProducts.map((prod, idx) => ({ prod, idx }));
                          const groups = [
                            {
                              key: 'one_time',
                              label: t('appStoreSettings.oneTime'),
                              hint: t('appStoreSettings.oneTimeGroupHint'),
                              items: rows.filter(r => r.prod.type !== 'subscription'),
                            },
                            {
                              key: 'subscription',
                              label: t('appStoreSettings.subscription'),
                              hint: t('appStoreSettings.subscriptionGroupHint'),
                              items: rows.filter(r => r.prod.type === 'subscription'),
                            },
                          ].filter(g => g.items.length > 0);
                          return (
                            <div className="space-y-5">
                              {groups.map(group => (
                                <div key={group.key}>
                                  <div className="mb-2 flex items-center gap-2">
                                    <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      {group.label}
                                    </h5>
                                    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 px-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                      {group.items.length}
                                    </span>
                                    <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                      {group.hint}
                                    </span>
                                  </div>
                                  <ul className="space-y-2">
                                    {group.items.map(({ prod, idx }) => renderProductRow(prod, idx))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                </>
              )}

              {currentTab === 'availability' && (
                <>
                  <div className="p-0 mb-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {t('panel.availabilityTitle')}
                      <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                        ({selectedPlatform === PLATFORM_IOS ? 'App Store · iOS' : 'Play Store · Android'})
                      </span>
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('panel.availabilityDescription')}
                    </p>
                  </div>
                  <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <fieldset className="space-y-2">
                      <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('panel.availabilityMode')}
                      </legend>
                      <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                        <input
                          type="radio"
                          name={`availability-mode-${selectedPlatform}`}
                          checked={currentPlatformAvailability.mode === 'all'}
                          onChange={() =>
                            setForm(f => ({
                              ...f,
                              availability: {
                                ...f.availability,
                                [selectedPlatform]: {
                                  ...(f.availability[selectedPlatform] ?? emptyPlatformAvailability()),
                                  mode: 'all',
                                },
                              },
                            }))
                          }
                        />
                        {t('panel.availabilityModeAll')}
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                        <input
                          type="radio"
                          name={`availability-mode-${selectedPlatform}`}
                          checked={currentPlatformAvailability.mode === 'selected'}
                          onChange={() =>
                            setForm(f => ({
                              ...f,
                              availability: {
                                ...f.availability,
                                [selectedPlatform]: {
                                  ...(f.availability[selectedPlatform] ?? emptyPlatformAvailability()),
                                  mode: 'selected',
                                },
                              },
                            }))
                          }
                        />
                        {t('panel.availabilityModeSelected')}
                      </label>
                    </fieldset>

                    {selectedPlatform === PLATFORM_IOS && (
                      <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                        <input
                          type="checkbox"
                          checked={currentPlatformAvailability.availableInNewTerritories}
                          onChange={e =>
                            setForm(f => ({
                              ...f,
                              availability: {
                                ...f.availability,
                                [selectedPlatform]: {
                                  ...(f.availability[selectedPlatform] ?? emptyPlatformAvailability()),
                                  availableInNewTerritories: e.target.checked,
                                },
                              },
                            }))
                          }
                          className="rounded border-gray-300 dark:border-gray-500 text-primary-600"
                        />
                        {t('panel.availabilityNewTerritoriesIOS')}
                      </label>
                    )}

                    {selectedPlatform === PLATFORM_ANDROID &&
                      currentPlatformAvailability.mode === 'selected' && (
                        <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                          <input
                            type="checkbox"
                            checked={currentPlatformAvailability.includeRestOfWorld}
                            onChange={e =>
                              setForm(f => ({
                                ...f,
                                availability: {
                                  ...f.availability,
                                  [selectedPlatform]: {
                                    ...(f.availability[selectedPlatform] ?? emptyPlatformAvailability()),
                                    includeRestOfWorld: e.target.checked,
                                  },
                                },
                              }))
                            }
                            className="rounded border-gray-300 dark:border-gray-500 text-primary-600"
                          />
                          {t('panel.availabilityRestOfWorldAndroid')}
                        </label>
                      )}

                    {selectedPlatform === PLATFORM_ANDROID && (
                      <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                        {t('panel.availabilityAndroidPlayNote')}
                      </p>
                    )}

                    {currentPlatformAvailability.mode === 'selected' && (
                      <div>
                        <input
                          type="text"
                          value={availabilitySearch}
                          onChange={e => setAvailabilitySearch(e.target.value)}
                          placeholder={t('panel.availabilitySearchPlaceholder')}
                          className="w-full mb-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() =>
                              setForm(f => ({
                                ...f,
                                availability: {
                                  ...f.availability,
                                  [selectedPlatform]: {
                                    ...(f.availability[selectedPlatform] ?? emptyPlatformAvailability()),
                                    territories: filteredAvailabilityOptions.map(o => o.value),
                                  },
                                },
                              }))
                            }
                            className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-300"
                          >
                            {t('panel.availabilitySelectVisible')}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setForm(f => ({
                                ...f,
                                availability: {
                                  ...f.availability,
                                  [selectedPlatform]: {
                                    ...(f.availability[selectedPlatform] ?? emptyPlatformAvailability()),
                                    territories: [],
                                  },
                                },
                              }))
                            }
                            className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-300"
                          >
                            {t('panel.availabilityClearSelection')}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          {t('panel.availabilitySelectedCount', {
                            count: currentPlatformAvailability.territories.length,
                          })}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                          {filteredAvailabilityOptions.map(opt => (
                            <label
                              key={opt.value}
                              className="flex items-center gap-2 text-sm py-1 px-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={currentPlatformAvailability.territories.includes(opt.value)}
                                onChange={() => {
                                  setForm(f => {
                                    const cur = f.availability[selectedPlatform] ?? emptyPlatformAvailability();
                                    const set = new Set(cur.territories);
                                    if (set.has(opt.value)) set.delete(opt.value);
                                    else set.add(opt.value);
                                    return {
                                      ...f,
                                      availability: {
                                        ...f.availability,
                                        [selectedPlatform]: { ...cur, territories: Array.from(set).sort() },
                                      },
                                    };
                                  });
                                }}
                                className="rounded border-gray-300 dark:border-gray-500 text-primary-600"
                              />
                              <span className="truncate" title={opt.label}>
                                {opt.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

            </form>
          </div>

          <OnboardingTour
            tourId="app-store-settings"
            steps={[
              { target: '[data-tour="settings-platform-switch"]', title: t('onboarding.appStoreSettings.step1Title'), body: t('onboarding.appStoreSettings.step1Body') },
              { target: '[data-tour="settings-price-tier"]', title: t('onboarding.appStoreSettings.step2Title'), body: t('onboarding.appStoreSettings.step2Body') },
              { target: '[data-tour="settings-iap"]', title: t('onboarding.appStoreSettings.step3Title'), body: t('onboarding.appStoreSettings.step3Body') },
              { target: '[data-tour="settings-save"]', title: t('onboarding.appStoreSettings.step4Title'), body: t('onboarding.appStoreSettings.step4Body') },
            ]}
            autoStart
          />

          <ModalLayout
            open={showPricingProductModal}
            onClose={() => setShowPricingProductModal(false)}
            title={editingPricingProductIndex !== null ? t('appStoreSettings.editProduct') : t('appStoreSettings.addInAppProduct')}
            icon={<DollarSign className="h-5 w-5 text-primary-600 dark:text-primary-400" />}
            primaryLabel={t('common.save')}
            onPrimary={handleSavePricingProduct}
            maxWidth="lg"
          >
                <div className="space-y-4">
                  {isUploadedProductEdit && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                      <Info className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{t('appStoreSettings.uploadedLockNote')}</span>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('appStoreSettings.productIdLabel')}
                    </label>
                    <input
                      type="text"
                      value={pricingProductForm.productId}
                      onChange={e => setPricingProductForm(f => ({ ...f, productId: e.target.value }))}
                      disabled={isUploadedProductEdit}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
                      placeholder="com.example.premium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('appStoreSettings.typeLabel')}</label>
                    <div className="flex gap-4">
                      <label className={`flex items-center gap-2 ${isUploadedProductEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="productType"
                          checked={pricingProductForm.type === 'one_time'}
                          onChange={() => setPricingProductForm(f => ({ ...f, type: 'one_time' }))}
                          disabled={isUploadedProductEdit}
                          className="rounded border-gray-300 text-primary-600"
                        />
                        <span className="text-sm">{t('appStoreSettings.oneTime')}</span>
                      </label>
                      <label className={`flex items-center gap-2 ${isUploadedProductEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="productType"
                          checked={pricingProductForm.type === 'subscription'}
                          onChange={() => setPricingProductForm(f => ({ ...f, type: 'subscription' }))}
                          disabled={isUploadedProductEdit}
                          className="rounded border-gray-300 text-primary-600"
                        />
                        <span className="text-sm">{t('appStoreSettings.subscription')}</span>
                      </label>
                    </div>
                  </div>
                  {pricingProductForm.type === 'subscription' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('appStoreSettings.subscriptionDuration')}
                          </label>
                          <select
                            value={pricingProductForm.subscriptionDuration ?? 'P1M'}
                            onChange={e =>
                              setPricingProductForm(f => ({ ...f, subscriptionDuration: e.target.value }))
                            }
                            disabled={isUploadedProductEdit}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
                          >
                            {SUBSCRIPTION_DURATIONS.map(d => (
                              <option key={d.value} value={d.value}>
                                {t(d.labelKey)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('pricing.trialDaysLabel')}
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={pricingProductForm.trialDays ?? 0}
                            onChange={e =>
                              setPricingProductForm(f => ({
                                ...f,
                                trialDays: parseInt(e.target.value, 10) || 0,
                              }))
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('pricing.introPriceLabel')}
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('pricing.introPriceHelp')}</p>
                        <select
                          value={pricingProductForm.introPriceTierByLocale?.[primaryLocale] ?? ''}
                          onChange={e => {
                            const value = e.target.value;
                            setPricingProductForm(f => ({
                              ...f,
                              introPriceTierByLocale: value
                                ? { ...(f.introPriceTierByLocale ?? {}), [primaryLocale]: value }
                                : Object.fromEntries(
                                    Object.entries(f.introPriceTierByLocale ?? {}).filter(
                                      ([loc]) => loc !== primaryLocale
                                    )
                                  ),
                            }));
                          }}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                        >
                          <option value="">{t('pricing.introPriceNone')}</option>
                          {APP_PRICE_TIERS.filter(tier => tier.value !== 'free').map(tier => (
                            <option key={tier.value} value={tier.value}>
                              {tier.labelKey ? t(tier.labelKey) : tier.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('pricing.productPrimaryLocale')}{' '}
                        <span className="font-normal text-gray-500 dark:text-gray-400">
                          ({getLocaleFlag(primaryLocale)} {primaryLocaleLabel})
                        </span>
                      </h4>
                      <button
                        type="button"
                        onClick={handleOpenProductAiTranslateModal}
                        disabled={productAiTranslating || !primaryHasContent || isUploadedProductEdit}
                        title={
                          isUploadedProductEdit
                            ? t('appStoreSettings.uploadedLockNote')
                            : !isPro
                              ? t('proFeature.requiresPro')
                              : !primaryHasContent
                                ? t('pricing.aiTranslateNeedPrimary')
                                : t('pricing.aiTranslateHint')
                        }
                        className={`inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors ${
                          isPro
                            ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 disabled:opacity-50 disabled:pointer-events-none'
                            : 'bg-primary-50 dark:bg-primary-950/50 text-gray-500 dark:text-gray-400 ring-1 ring-inset ring-primary-200/90 dark:ring-primary-800/70 cursor-pointer'
                        }`}
                      >
                        <Sparkles className="h-4 w-4 shrink-0" />
                        {t('pricing.aiTranslate')}
                        {!isPro && <ProTag />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      {t('pricing.aiTranslateHint')}
                    </p>
                    <div className="p-3 rounded-lg border border-primary-200 dark:border-primary-800/60 bg-primary-50/40 dark:bg-primary-900/10 space-y-2">
                      <input
                        type="text"
                        value={pricingProductForm.nameLocalized?.[primaryLocale] ?? ''}
                        onChange={e =>
                          setPricingProductForm(f => ({
                            ...f,
                            nameLocalized: {
                              ...(f.nameLocalized ?? {}),
                              [primaryLocale]: e.target.value,
                            },
                          }))
                        }
                        disabled={isUploadedProductEdit}
                        placeholder={t('pricing.productName')}
                        className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
                      />
                      <input
                        type="text"
                        value={pricingProductForm.descriptionLocalized?.[primaryLocale] ?? ''}
                        onChange={e =>
                          setPricingProductForm(f => ({
                            ...f,
                            descriptionLocalized: {
                              ...(f.descriptionLocalized ?? {}),
                              [primaryLocale]: e.target.value,
                            },
                          }))
                        }
                        disabled={isUploadedProductEdit}
                        placeholder={t('pricing.productDescription')}
                        className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
                      />
                      <select
                        value={pricingProductForm.priceTierByLocale?.[primaryLocale] ?? 'free'}
                        onChange={e =>
                          setPricingProductForm(f => ({
                            ...f,
                            priceTierByLocale: {
                              ...(f.priceTierByLocale ?? {}),
                              [primaryLocale]: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                        aria-label={t('pricing.productPrice')}
                      >
                        {APP_PRICE_TIERS.map(tier => (
                          <option key={tier.value} value={tier.value}>
                            {tier.labelKey ? t(tier.labelKey) : tier.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <RegionTierPriceEditor
                      value={pricingProductForm.priceByRegionTier ?? {}}
                      onChange={next => setPricingProductForm(f => ({ ...f, priceByRegionTier: next }))}
                    />
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() => setShowOtherLocales(v => !v)}
                      className="flex w-full items-center justify-between gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 py-1"
                    >
                      <span>
                        {t('pricing.otherLocales')}
                        {filledOtherLocaleCount > 0 && (
                          <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
                            ({t('pricing.otherLocalesFilled', { count: filledOtherLocaleCount })})
                          </span>
                        )}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                        {showOtherLocales ? t('pricing.hideOtherLocales') : t('pricing.showOtherLocales')}
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${showOtherLocales ? 'rotate-180' : ''}`}
                        />
                      </span>
                    </button>
                    {showOtherLocales && (
                      <div className="mt-2 space-y-3 max-h-64 overflow-y-auto">
                        {otherLocales.map(loc => (
                          <div
                            key={loc.value}
                            className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 space-y-2"
                          >
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              {getLocaleFlag(loc.value)} {loc.label}
                            </div>
                            <input
                              type="text"
                              value={pricingProductForm.nameLocalized?.[loc.value] ?? ''}
                              onChange={e =>
                                setPricingProductForm(f => ({
                                  ...f,
                                  nameLocalized: {
                                    ...(f.nameLocalized ?? {}),
                                    [loc.value]: e.target.value,
                                  },
                                }))
                              }
                              disabled={isUploadedProductEdit}
                              placeholder={t('pricing.productName')}
                              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
                            />
                            <input
                              type="text"
                              value={pricingProductForm.descriptionLocalized?.[loc.value] ?? ''}
                              onChange={e =>
                                setPricingProductForm(f => ({
                                  ...f,
                                  descriptionLocalized: {
                                    ...(f.descriptionLocalized ?? {}),
                                    [loc.value]: e.target.value,
                                  },
                                }))
                              }
                              disabled={isUploadedProductEdit}
                              placeholder={t('pricing.productDescription')}
                              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
          </ModalLayout>

          <ModalLayout
            open={showProductAiTranslateModal}
            onClose={() => !productAiTranslating && setShowProductAiTranslateModal(false)}
            title={t('pricing.aiTranslateModalTitle')}
            icon={<Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />}
            description={t('pricing.aiTranslateSource', {
              locale: `${getLocaleFlag(primaryLocale)} ${primaryLocaleLabel}`,
            })}
            primaryLabel={productAiTranslating ? t('pricing.aiTranslateRunning') : t('pricing.aiTranslateStart', { count: productAiTargetLocales.length })}
            onPrimary={handleProductAiTranslate}
            primaryDisabled={productAiTargetLocales.length === 0}
            primaryLoading={productAiTranslating}
            closeDisabled={productAiTranslating}
          >
            <TargetLocaleChecklist
              selected={productAiTargetLocales}
              onChange={setProductAiTargetLocales}
              excludeLocales={[primaryLocale]}
              disabled={productAiTranslating}
            />
          </ModalLayout>

          <PricingAIChatPanel
            open={aiAdvisorOpen}
            onClose={() => setAiAdvisorOpen(false)}
            appId={appId ?? ''}
            platform={selectedPlatform}
            pricing={aiAdvisorPricing}
            onApplySuggestion={handleApplyPricingSuggestions}
          />

          {aiApplyNotice && (
            <div className="fixed bottom-4 left-4 z-50 max-w-xs rounded-lg border border-green-300 bg-green-50 px-3.5 py-2.5 text-sm font-medium text-green-800 shadow-lg dark:border-green-700 dark:bg-green-900/40 dark:text-green-200">
              {aiApplyNotice}
            </div>
          )}
        </>
      )}
    </AppPageLayout>
  );
};

export default AppStoreSettingsPage;
